/**
 * 인증 관리 훅
 *
 * 주요 기능:
 * - 이메일 및 소셜 로그인/로그아웃 처리
 * - 회원가입 및 사용자 프로필 관리
 * - 계정 탈퇴 및 이름 업데이트
 * - 구글, 카카오 소셜 인증 연동 및 해제
 *
 * 지원 인증 방식:
 * - 이메일/비밀번호 로그인
 * - 구글 소셜 로그인
 * - 카카오 소셜 로그인
 *
 * 상태 관리:
 * - Zustand 기반 전역 인증 상태 (useAuthStore)
 * - 토큰 기반 세션 관리 (tokenService)
 * - 로컬스토리지 및 세션스토리지 활용
 *
 * 사용처:
 * - 로그인/회원가입 모달 컴포넌트
 * - 프로필 설정 페이지
 * - 계정 관리 페이지
 */

import {
  login as loginService,
  logout as logoutService,
  signup as signupService,
} from "@/services/auth/emailAuthService";
import {
  revokeGoogleAuth,
  revokeKakaoAuth,
} from "@/services/auth/socialAuthService";
import {
  deleteAccount,
  getProfile,
  updateName,
} from "@/services/auth/userService";
import { useAuthStore } from "@/stores/authStore";
import { User } from "@/types/app_types";
import { unlinkKakaoAccount } from "@/utils/kakaoAuth";
import { logger } from "@/utils/logger";
import { tokenService } from "@/utils/tokenService";

/**
 * 인증 관련 기능을 제공하는 커스텀 훅
 *
 * 로그인, 로그아웃, 회원가입, 프로필 관리 등 인증 관련 모든 기능 제공
 *
 * @returns 인증 관련 함수들
 */

export const useAuth = () => {
  const { setUser, clearUser } = useAuthStore();

  /**
   * 이메일 로그인
   *
   * @param email - 사용자 이메일
   * @param password - 사용자 비밀번호
   * @returns 로그인 결과 (성공 시 사용자 정보 포함)
   */
  const login = async (email: string, password: string) => {
    try {
      const response = await loginService(email, password);

      // 로그인 성공
      if (response.success && response.data) {
        const { user } = response.data;

        // 토큰은 이미 loginService 내부에서 저장됨
        // 사용자 정보만 전역 상태에 저장
        setUser(user);

        return {
          success: true,
          user,
          message: response.message,
        };
      }

      // 로그인 실패
      return {
        success: false,
        message: response.message,
      };
    } catch (error) {
      logger.error("로그인 실패", { error, email });

      return {
        success: false,
        error: "An error occurred during login. Please try again.",
      };
    }
  };

  /**
   * 구글 로그아웃 처리
   *
   * 구글 계정 자동 선택 비활성화
   */
  const logoutFromGoogle = async () => {
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }
  };

  /**
   * 카카오 로그아웃 처리
   *
   * 카카오 SDK를 통한 로그아웃 수행
   */
  const logoutFromKakao = async () => {
    return new Promise<boolean>((resolve) => {
      if (!window.Kakao.Auth.getAccessToken()) {
        resolve(true);
        return;
      }

      window.Kakao.Auth.logout(() => {
        resolve(true);
      });
    });
  };

  /**
   * 로그아웃
   *
   * 소셜 로그인 provider별 로그아웃 처리 및 전역 상태 초기화
   *
   * @returns 로그아웃 결과
   */
  const logout = async () => {
    try {
      const user = useAuthStore.getState().user;

      // 소셜 로그인 provider별 로그아웃 처리
      if (user?.provider === "google") {
        await logoutFromGoogle();
      } else if (user?.provider === "kakao") {
        await logoutFromKakao();
      }

      // 백엔드 로그아웃 API 호출
      const response = await logoutService();

      // 로그아웃 성공 시 쿠키 삭제 및 로컬 상태 초기화
      if (response._meta?.status === 200) {
        // 모든 쿠키 삭제 (토큰 포함)
        tokenService.clearAllCookies();

        // 로컬스토리지 정리
        localStorage.removeItem("themeMode");
        localStorage.removeItem("mockUsers");
        sessionStorage.clear();

        // 카카오 SDK 로그아웃 처리
        if (window.Kakao?.Auth?.getAccessToken()) {
          window.Kakao.Auth.logout(() => {});
        }

        // 전역 상태 초기화
        clearUser();
        // 장바구니는 페이지 새로고침 시 자동으로 초기화됨
        window.location.href = "/";
        return { success: true };
      } else {
        logger.error("로그아웃 실패", { message: response.message });
        return { success: false, error: response.message };
      }
    } catch (err) {
      logger.error("로그아웃 오류", { error: err });
      return {
        success: false,
        error: "An error occurred during logout. Please try again.",
      };
    }
  };

  /**
   * 회원가입
   *
   * @param signupData - 회원가입 데이터 (이메일, 비밀번호, 이름, 인증 코드)
   * @returns 회원가입 결과
   */
  const signup = async (signupData: {
    email: string;
    password1: string;
    password2: string;
    confirm_code: string;
    full_name: string;
  }) => {
    try {
      const response = await signupService(signupData);

      if (response.success && response.data) {
        // 사용자 정보 전역 상태에 저장
        setUser(response.data.user);

        return {
          success: true,
          message: response.message,
          data: response.data,
        };
      }

      return {
        success: false,
        message: response.message,
      };
    } catch (error) {
      logger.error("회원가입 실패", { error, signupData });
      return {
        success: false,
        error: "An error occurred during signup. Please try again.",
      };
    }
  };

  /**
   * 사용자 프로필 조회
   *
   * 중복 호출 방지 로직 포함:
   * - 이미 프로필이 로드된 경우 기존 데이터 반환
   * - 로딩 중인 경우 null 반환
   *
   * @returns 사용자 프로필 데이터 또는 null
   */
  const getUserProfile = async () => {
    // 중복 호출 방지: 이미 user가 있거나 fetching 중이면 스킵
    const { user, isProfileFetching, isProfileFetched } =
      useAuthStore.getState();

    if (user && isProfileFetched) {
      return user;
    }

    if (isProfileFetching) {
      return null;
    }

    try {
      const response = await getProfile();

      if (response.success && response.data) {
        // 백엔드 응답 데이터를 User 타입으로 변환
        const user: User = {
          id: response.data.id,
          email: response.data.email,
          name: response.data.name,
          provider: response.data.provider as "email" | "google" | "kakao",
          walletAddress: null,
          ensName: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          isActive: true,
          profile: {
            nftsOwned: 0,
            totalPurchases: 0,
          },
        };

        setUser(user);
        return user;
      } else {
        throw new Error(response.message || "Failed to fetch user profile");
      }
    } catch (error) {
      logger.error("사용자 프로필 조회 실패", { error });
      return null;
    }
  };

  /**
   * 사용자 이름 업데이트
   *
   * @param full_name - 새로운 사용자 이름
   * @returns 업데이트 결과
   */
  const updateUserName = async (
    full_name: string,
  ): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await updateName(full_name);

      if (response.success && response.data) {
        // API 응답 데이터로 전역 상태 업데이트
        const currentUser = useAuthStore.getState().user;
        if (currentUser) {
          setUser({
            ...currentUser,
            id: response.data.id,
            name: response.data.name,
            email: response.data.email,
            provider: response.data.provider as "email" | "google" | "kakao",
          });
        }

        return {
          success: true,
          message: response.message || "User name has been updated.",
        };
      }

      return {
        success: false,
        message: response.message || "Failed to update user name.",
      };
    } catch (error) {
      logger.error("이름 업데이트 실패", { error, full_name });
      return {
        success: false,
        message: "An error occurred while updating name. Please try again.",
      };
    }
  };

  /**
   * 계정 탈퇴
   *
   * 소셜 로그인 연동 해제 및 전체 데이터 삭제
   *
   * 처리 순서:
   * 1. 소셜 로그인 provider별 연결 해제 (구글/카카오)
   * 2. 백엔드 회원 탈퇴 API 호출
   * 3. 로컬 스토리지 및 세션 스토리지 전체 삭제
   * 4. 전역 상태 초기화 및 홈으로 리다이렉트
   *
   * @returns 계정 탈퇴 결과
   */
  const deleteAuthAccount = async () => {
    try {
      const user = useAuthStore.getState().user;

      if (!user) {
        return { success: false, error: "No logged in user." };
      }

      // 소셜 로그인 provider별 연결 해제 처리
      if (user.provider === "google") {
        const googleRevokeResult = await revokeGoogleAuth(user.id);
        if (!googleRevokeResult.success) {
          logger.error("구글 계정 연결 해제 실패", {
            message: googleRevokeResult.message,
            userId: user.id,
          });
        }

        if (window.google?.accounts?.id) {
          window.google.accounts.id.disableAutoSelect();
        }
      } else if (user.provider === "kakao") {
        const kakaoResult = await unlinkKakaoAccount();
        if (!kakaoResult.success) {
          logger.error("카카오 계정 연결 해제 실패", {
            error: kakaoResult.error,
            userId: user.id,
          });
        }

        const kakaoRevokeResult = await revokeKakaoAuth(user.id);
        if (!kakaoRevokeResult.success) {
          logger.error("카카오 백엔드 연결 해제 실패", {
            message: kakaoRevokeResult.message,
            userId: user.id,
          });
        }
      }

      // 백엔드 회원 탈퇴 API 호출
      const response = await deleteAccount(true);

      if (response.success) {
        // 토큰 정리
        tokenService.removeTokens();

        // 로컬 스토리지 정리
        localStorage.removeItem("themeMode");
        localStorage.removeItem("mockUsers");
        localStorage.removeItem("cartItems");
        localStorage.removeItem("recentlyViewed");
        sessionStorage.clear();

        // 카카오 SDK 정리
        if (window.Kakao?.Auth?.getAccessToken()) {
          window.Kakao.Auth.logout();
        }

        // 전역 상태 초기화
        clearUser();

        // 홈으로 리다이렉트
        window.location.href = "/";

        return { success: true };
      } else {
        return { success: false, error: response.message };
      }
    } catch (error) {
      logger.error("계정 삭제 오류", { error });
      return {
        success: false,
        error: "An error occurred while deleting account. Please try again.",
      };
    }
  };

  return {
    login,
    logout,
    signup,
    getUserProfile,
    updateUserName,
    deleteAuthAccount,
  };
};
